import React, { useState, useEffect } from 'react';
import { PageSkeleton } from '../DashboardLoader';
import {
  Star,
  MessageSquare,
  Search,
  Reply,
  Flag,
  User,
  Loader2,
  AlertCircle,
  CheckCircle
} from 'lucide-react';
import { restaurantManagementApi } from '../../../services/restaurantManagementApi';

const PRIMARY = '#054625';
const TERRA = '#EF4C23';
const MUSTARD = '#FFC717';

const NewRestaurantReviews = () => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRating, setFilterRating] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedReview, setSelectedReview] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [replying, setReplying] = useState(false);

  // Statistiques des avis
  const [stats, setStats] = useState({
    total: 0,
    average: 0,
    distribution: { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 }
  });

  // Charger les avis
  useEffect(() => {
    const fetchReviews = async () => {
      try {
        setLoading(true);
        const data = await restaurantManagementApi.getReviews();
        setReviews(data.reviews || []);
        setStats(data.stats || stats);
      } catch (err) {
        console.error('Erreur lors du chargement des avis:', err);
        setError('Erreur lors du chargement des avis');
      } finally {
        setLoading(false);
      }
    };

    fetchReviews();
  }, []);

  // Filtrer les avis
  const filteredReviews = reviews.filter(review => {
    const matchesSearch = review.comment?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         review.client_name?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRating = filterRating === 'all' || review.rating === parseInt(filterRating);
    const matchesStatus = filterStatus === 'all' ||
                         (filterStatus === 'replied' && review.reply) ||
                         (filterStatus === 'pending' && !review.reply);

    return matchesSearch && matchesRating && matchesStatus;
  });

  // Répondre à un avis
  const handleReply = async (reviewId) => {
    if (!replyText.trim()) return;

    try {
      setReplying(true);
      await restaurantManagementApi.replyToReview(reviewId, replyText);

      // Mettre à jour l'avis localement
      setReviews(prev => prev.map(review =>
        review.id === reviewId
          ? { ...review, reply: replyText, reply_date: new Date().toISOString() }
          : review
      ));

      setReplyText('');
      setSelectedReview(null);
      setSuccess('Réponse envoyée avec succès');
    } catch (err) {
      console.error('Erreur lors de l\'envoi de la réponse:', err);
      setError('Erreur lors de l\'envoi de la réponse');
    } finally {
      setReplying(false);
    }
  };

  // Signaler un avis
  const handleReportReview = async (reviewId) => {
    try {
      await restaurantManagementApi.reportReview(reviewId);
      setSuccess('Avis signalé avec succès');
    } catch (err) {
      console.error('Erreur lors du signalement:', err);
      setError('Erreur lors du signalement de l\'avis');
    }
  };

  // Rendre les étoiles
  const renderStars = (rating) => {
    return Array.from({ length: 5 }, (_, i) => (
      <Star
        key={i}
        size={14}
        style={{ color: i < rating ? MUSTARD : 'rgba(5,16,4,0.16)' }}
        fill="currentColor"
      />
    ));
  };

  // Formater la date
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) return <PageSkeleton />;

  const repliedCount = reviews.filter(r => r.reply).length;
  const pendingCount = reviews.filter(r => !r.reply).length;

  return (
    <div style={{ padding: 'clamp(20px, 2.6vw, 34px) clamp(18px, 2.6vw, 36px) clamp(36px, 4vw, 60px)', display: 'flex', flexDirection: 'column', gap: 'clamp(16px, 2vw, 24px)' }}>
      {/* En-tête */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
        <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '12.5px', fontWeight: 600, letterSpacing: '0.09em', textTransform: 'uppercase', color: TERRA }}>
          {stats.total} avis · note moyenne {stats.average.toFixed(1)}
        </span>
        <h1 style={{ margin: 0, fontFamily: 'Fraunces, Georgia, serif', fontWeight: 900, fontSize: 'clamp(28px, 3.4vw, 44px)', lineHeight: 1, letterSpacing: '-0.025em' }}>Avis clients</h1>
      </div>

      {error && (
        <div className="flex items-center gap-2" style={{ background: '#FEF0EA', color: '#7A200B', borderRadius: '18px', padding: '14px 18px', fontSize: '14px' }}>
          <AlertCircle size={18} />
          {error?.message || error?.toString() || 'Une erreur est survenue'}
        </div>
      )}
      {success && (
        <div className="flex items-center gap-2" style={{ background: '#DDEECB', color: PRIMARY, borderRadius: '18px', padding: '14px 18px', fontSize: '14px' }}>
          <CheckCircle size={18} />
          {success}
        </div>
      )}

      {/* Statistiques */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 180px), 1fr))', gap: 'clamp(12px, 1.6vw, 18px)' }}>
        <div style={{ background: '#FCFBF5', borderRadius: '26px', padding: '20px 22px', display: 'flex', flexDirection: 'column', gap: '5px' }}>
          <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '12px', fontWeight: 600, letterSpacing: '0.09em', textTransform: 'uppercase', color: 'rgba(5,16,4,0.55)' }}>Note moyenne</span>
          <strong style={{ fontFamily: 'Fraunces, Georgia, serif', fontWeight: 900, fontSize: '30px', letterSpacing: '-0.02em' }}>{stats.average.toFixed(1)}</strong>
        </div>
        <div style={{ background: '#FCFBF5', borderRadius: '26px', padding: '20px 22px', display: 'flex', flexDirection: 'column', gap: '5px' }}>
          <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '12px', fontWeight: 600, letterSpacing: '0.09em', textTransform: 'uppercase', color: 'rgba(5,16,4,0.55)' }}>Total avis</span>
          <strong style={{ fontFamily: 'Fraunces, Georgia, serif', fontWeight: 900, fontSize: '30px', letterSpacing: '-0.02em' }}>{stats.total}</strong>
        </div>
        <div style={{ background: '#FCFBF5', borderRadius: '26px', padding: '20px 22px', display: 'flex', flexDirection: 'column', gap: '5px' }}>
          <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '12px', fontWeight: 600, letterSpacing: '0.09em', textTransform: 'uppercase', color: 'rgba(5,16,4,0.55)' }}>Avec réponse</span>
          <strong style={{ fontFamily: 'Fraunces, Georgia, serif', fontWeight: 900, fontSize: '30px', letterSpacing: '-0.02em', color: PRIMARY }}>{repliedCount}</strong>
        </div>
        <div style={{ background: '#FCFBF5', borderRadius: '26px', padding: '20px 22px', display: 'flex', flexDirection: 'column', gap: '5px' }}>
          <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '12px', fontWeight: 600, letterSpacing: '0.09em', textTransform: 'uppercase', color: 'rgba(5,16,4,0.55)' }}>En attente</span>
          <strong style={{ fontFamily: 'Fraunces, Georgia, serif', fontWeight: 900, fontSize: '30px', letterSpacing: '-0.02em', color: TERRA }}>{pendingCount}</strong>
        </div>
      </div>

      {/* Distribution des notes */}
      <div style={{ background: '#FCFBF5', borderRadius: '30px', padding: 'clamp(18px, 2.2vw, 26px)', display: 'flex', flexDirection: 'column', gap: '14px' }}>
        <h2 style={{ margin: 0, fontFamily: 'Fraunces, Georgia, serif', fontWeight: 900, fontSize: '20px', letterSpacing: '-0.02em' }}>Distribution des notes</h2>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {[5, 4, 3, 2, 1].map(rating => {
            const count = stats.distribution[rating] || 0;
            const percentage = stats.total > 0 ? (count / stats.total) * 100 : 0;

            return (
              <div key={rating} className="flex items-center gap-3">
                <span style={{ width: '28px', fontFamily: 'Archivo, sans-serif', fontSize: '13px', color: 'rgba(5,16,4,0.6)' }}>{rating}★</span>
                <span style={{ flex: 1, display: 'block', height: '8px', borderRadius: '999px', background: '#E5E2CB' }}>
                  <span style={{ display: 'block', height: '8px', width: `${percentage}%`, borderRadius: '999px', background: MUSTARD, transition: 'width 300ms ease' }} />
                </span>
                <span style={{ width: '32px', textAlign: 'right', fontFamily: 'Archivo, sans-serif', fontSize: '13px', color: 'rgba(5,16,4,0.6)' }}>{count}</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Filtres et recherche */}
      <div className="flex flex-col sm:flex-row gap-3">
        <label className="flex-1 flex items-center gap-2.5" style={{ background: '#FCFBF5', borderRadius: '999px', padding: '0 20px', minHeight: '46px' }}>
          <Search size={16} style={{ color: PRIMARY, flexShrink: 0 }} />
          <input
            type="text"
            placeholder="Rechercher dans les avis..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ flex: 1, minWidth: 0, border: 'none', background: 'transparent', fontSize: '14.5px', color: '#051004', outline: 'none' }}
          />
        </label>

        <select
          value={filterRating}
          onChange={(e) => setFilterRating(e.target.value)}
          className="sm:min-w-[10rem]"
          style={{ background: '#FCFBF5', border: 'none', borderRadius: '999px', padding: '0 18px', minHeight: '46px', fontSize: '13.5px', color: '#051004', outline: 'none' }}
        >
          <option value="all">Toutes les notes</option>
          <option value="5">5 étoiles</option>
          <option value="4">4 étoiles</option>
          <option value="3">3 étoiles</option>
          <option value="2">2 étoiles</option>
          <option value="1">1 étoile</option>
        </select>

        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="sm:min-w-[10rem]"
          style={{ background: '#FCFBF5', border: 'none', borderRadius: '999px', padding: '0 18px', minHeight: '46px', fontSize: '13.5px', color: '#051004', outline: 'none' }}
        >
          <option value="all">Tous les statuts</option>
          <option value="pending">En attente</option>
          <option value="replied">Répondus</option>
        </select>
      </div>

      {/* Liste des avis */}
      <div style={{ background: '#FCFBF5', borderRadius: '30px', padding: 'clamp(18px, 2.2vw, 26px)', display: 'flex', flexDirection: 'column', gap: '14px' }}>
        <div className="flex items-center gap-3 flex-wrap">
          <h2 style={{ margin: 0, fontFamily: 'Fraunces, Georgia, serif', fontWeight: 900, fontSize: '22px', letterSpacing: '-0.02em' }}>Avis</h2>
          <span style={{ marginLeft: 'auto', fontFamily: 'Archivo, sans-serif', fontSize: '13px', color: 'rgba(5,16,4,0.64)' }}>{filteredReviews.length} avis</span>
        </div>

        {filteredReviews.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '48px 16px' }}>
            <MessageSquare size={40} style={{ color: 'rgba(5,16,4,0.18)', margin: '0 auto 14px' }} />
            <h3 style={{ margin: '0 0 6px', fontFamily: 'Fraunces, Georgia, serif', fontWeight: 600, fontSize: '18px' }}>Aucun avis trouvé</h3>
            <p style={{ margin: 0, fontSize: '14px', color: 'rgba(5,16,4,0.5)' }}>Essayez d'ajuster votre recherche ou vos filtres.</p>
          </div>
        ) : (
          filteredReviews.map((review) => (
            <div key={review.id} style={{ background: '#FBF9D9', borderRadius: '24px', padding: '18px 20px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-3">
                  <span style={{ flex: 'none', width: '40px', height: '40px', borderRadius: '999px', background: '#72B744', display: 'grid', placeItems: 'center' }}>
                    <User size={18} style={{ color: '#051004' }} />
                  </span>
                  <span style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                    <strong style={{ fontSize: '14.5px' }}>{review.client_name || 'Client anonyme'}</strong>
                    <span className="flex items-center gap-2">
                      <span className="flex items-center gap-0.5">{renderStars(review.rating)}</span>
                      <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '12px', color: 'rgba(5,16,4,0.55)' }}>{formatDate(review.created_at)}</span>
                    </span>
                  </span>
                </div>

                <div className="flex items-center gap-2">
                  {!review.reply && (
                    <button
                      type="button"
                      onClick={() => setSelectedReview(review.id)}
                      className="inline-flex items-center gap-1.5"
                      style={{ border: 'none', cursor: 'pointer', background: PRIMARY, color: '#FBF9D9', fontFamily: 'Poppins, sans-serif', fontSize: '13px', fontWeight: 600, minHeight: '36px', padding: '0 16px', borderRadius: '999px' }}
                    >
                      <Reply size={14} />
                      Répondre
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => handleReportReview(review.id)}
                    className="inline-flex items-center gap-1.5"
                    style={{ border: '1px solid rgba(163,44,15,0.3)', cursor: 'pointer', background: 'transparent', color: '#A32C0F', fontFamily: 'Poppins, sans-serif', fontSize: '13px', fontWeight: 600, minHeight: '36px', padding: '0 16px', borderRadius: '999px' }}
                  >
                    <Flag size={14} />
                    Signaler
                  </button>
                </div>
              </div>

              {review.comment && (
                <p style={{ margin: 0, fontSize: '14px', lineHeight: 1.6, color: 'rgba(5,16,4,0.82)' }}>{review.comment}</p>
              )}

              {/* Réponse du restaurant */}
              {review.reply && (
                <div style={{ background: '#FCFBF5', borderRadius: '18px', padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  <span className="flex items-center gap-2">
                    <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '12.5px', fontWeight: 600, color: PRIMARY }}>Réponse du restaurant</span>
                    <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '11.5px', color: 'rgba(5,16,4,0.5)' }}>{formatDate(review.reply_date)}</span>
                  </span>
                  <p style={{ margin: 0, fontSize: '13.5px', lineHeight: 1.5, color: '#051004' }}>{review.reply}</p>
                </div>
              )}

              {/* Formulaire de réponse */}
              {selectedReview === review.id && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  <textarea
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                    placeholder="Tapez votre réponse..."
                    rows={3}
                    style={{ border: '1px solid rgba(5,16,4,0.14)', borderRadius: '18px', padding: '12px 16px', fontFamily: 'Poppins, sans-serif', fontSize: '14px', background: '#FCFBF5', color: '#051004', resize: 'vertical', outline: 'none' }}
                  />
                  <div className="flex justify-end gap-2">
                    <button
                      type="button"
                      onClick={() => { setSelectedReview(null); setReplyText(''); }}
                      style={{ border: 'none', cursor: 'pointer', background: 'transparent', color: 'rgba(5,16,4,0.6)', fontFamily: 'Poppins, sans-serif', fontSize: '13.5px', fontWeight: 600, padding: '0 8px' }}
                    >
                      Annuler
                    </button>
                    <button
                      type="button"
                      onClick={() => handleReply(review.id)}
                      disabled={replying || !replyText.trim()}
                      className="inline-flex items-center gap-2"
                      style={{ border: 'none', cursor: 'pointer', background: TERRA, color: '#FCFBF5', fontFamily: 'Poppins, sans-serif', fontSize: '13.5px', fontWeight: 600, minHeight: '40px', padding: '0 20px', borderRadius: '999px', opacity: (replying || !replyText.trim()) ? 0.5 : 1 }}
                    >
                      {replying ? <Loader2 size={15} className="animate-spin" /> : <Reply size={15} />}
                      {replying ? 'Envoi...' : 'Envoyer'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default NewRestaurantReviews;
